import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  MenuItem,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useFetch, useApiCall, useApiErrorNotify } from "@/hooks";
import { attendancesApi, membersApi } from "@/api";
import { useNotification } from "@/context/NotificationContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import PageLoadingState from "@/components/PageLoadingState";
import { DataGridActionsCell } from "@/components/DataGridActionsCell";
import {
  DATA_GRID_INITIAL_PAGE_SIZE,
  DATA_GRID_PAGE_SIZE_OPTIONS,
} from "@/constants";
import { EMPTY_CELL_PLACEHOLDER } from "@/constants/display";
import type { Attendance, Member } from "@/types";

const EMPTY: Partial<Attendance> = { memberId: "", startedAt: "", endedAt: "" };

export default function Attendances() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const onApiError = useApiErrorNotify();
  const {
    data: attendances,
    loading,
    error,
    refresh,
  } = useFetch<Attendance[]>(attendancesApi.getAttendances);
  const { data: members } = useFetch<Member[]>(membersApi.getMembers);

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Attendance>>(EMPTY);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { execute: doCreate, loading: creating } = useApiCall(
    attendancesApi.createAttendance,
    { onError: onApiError },
  );
  const { execute: doUpdate, loading: updating } = useApiCall(
    attendancesApi.updateAttendance,
    { onError: onApiError },
  );
  const { execute: doDelete, loading: deleting } = useApiCall(
    attendancesApi.deleteAttendance,
    { onError: onApiError },
  );

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    setFormOpen(true);
  };
  const openEdit = (a: Attendance) => {
    setEditId(a.id);
    setForm({
      memberId: a.memberId,
      startedAt: a.startedAt?.substring(0, 16) ?? "",
      endedAt: a.endedAt?.substring(0, 16) ?? "",
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    const payload = { ...form };
    if (!payload.endedAt) delete payload.endedAt;
    const ok = editId
      ? await doUpdate(editId, payload)
      : await doCreate(payload);
    if (ok !== undefined) {
      notify(t("common.success"), "success");
      setFormOpen(false);
      refresh();
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const ok = await doDelete(deleteId);
    if (ok !== undefined) {
      notify(t("common.success"), "success");
      setDeleteId(null);
      refresh();
    }
  };

  const setField = useCallback((field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const memberMap = useMemo(() => {
    const map = new Map<string, string>();
    (members ?? []).forEach((m) => map.set(m.id, m.name));
    return map;
  }, [members]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "memberId",
        headerName: t("common.member"),
        flex: 1,
        minWidth: 150,
        valueGetter: (_value: unknown, row: Attendance) =>
          memberMap.get(row.memberId) ?? row.memberId,
      },
      {
        field: "startedAt",
        headerName: t("common.startedAt"),
        flex: 1,
        minWidth: 170,
        valueFormatter: (v: string) =>
          v ? new Date(v).toLocaleString() : EMPTY_CELL_PLACEHOLDER,
      },
      {
        field: "endedAt",
        headerName: t("common.endedAt"),
        flex: 1,
        minWidth: 170,
        valueFormatter: (v: string) =>
          v ? new Date(v).toLocaleString() : EMPTY_CELL_PLACEHOLDER,
      },
      {
        field: "actions",
        headerName: t("common.actions"),
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <DataGridActionsCell
            onEdit={() => openEdit(params.row)}
            onDelete={() => setDeleteId(params.row.id)}
          />
        ),
      },
    ],
    [t, memberMap],
  );

  if (loading) return <PageLoadingState />;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("nav.attendances")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          {t("common.create")}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <DataGrid
        rows={Array.isArray(attendances) ? attendances : []}
        columns={columns}
        pageSizeOptions={[...DATA_GRID_PAGE_SIZE_OPTIONS]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: DATA_GRID_INITIAL_PAGE_SIZE },
          },
        }}
        disableRowSelectionOnClick
        disableColumnResize
      />
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editId ? t("common.edit") : t("common.create")}
        </DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              mt: 1,
            }}
          >
            <TextField
              label={t("common.member")}
              select
              value={form.memberId}
              onChange={(e) => setField("memberId", e.target.value)}
              fullWidth
              size="small"
              required
            >
              <MenuItem value="">{EMPTY_CELL_PLACEHOLDER}</MenuItem>
              {(members ?? []).map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t("common.startedAt")}
              type="datetime-local"
              value={form.startedAt}
              onChange={(e) => setField("startedAt", e.target.value)}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              required
            />
            <TextField
              label={t("common.endedAt")}
              type="datetime-local"
              value={form.endedAt}
              onChange={(e) => setField("endedAt", e.target.value)}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={creating || updating}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!deleteId}
        title={t("common.delete")}
        message={t("dialogs.deleteConfirm")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        loading={deleting}
      />
    </Box>
  );
}
